import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, Search, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import SearchOverlay from './SearchOverlay';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoZoomed, setIsLogoZoomed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, unreadCount } = useCustomerAuth();

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    // Trigger zoom animation
    setIsLogoZoomed(true);
    // Reset after 2 seconds
    setTimeout(() => {
      setIsLogoZoomed(false);
    }, 2000);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm shadow-lg"
    >
      {/* Brand strip above the nav (fixed 32px height) */}
      <div className="h-8 flex items-center justify-center gap-2 bg-gradient-to-r from-black via-gray-800 to-gray-600 px-4 overflow-hidden">
        <span className="text-white font-semibold text-xs md:text-sm whitespace-nowrap">The Navkar Travels by TVL</span>
        <span className="text-gray-500 hidden sm:inline">|</span>
        <span className="text-amber-300 italic text-xs whitespace-nowrap hidden sm:inline">Crafting Journeys, Creating Memories</span>
      </div>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo with zoom animation */}
          <div 
            className="flex items-center cursor-pointer" 
            onClick={handleLogoClick}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_adventure-leisure/artifacts/2fczd4n3_TNT%20BY%20TVLL.png"
              alt="The Navkar Travels by TVL"
              className={`h-20 w-auto md:h-24 transition-all duration-500 ease-out ${
                isLogoZoomed 
                  ? 'scale-150 md:scale-[1.8] drop-shadow-[0_0_20px_rgba(245,158,11,0.5)]' 
                  : 'scale-100 hover:scale-110'
              }`}
              style={{
                transformOrigin: 'left center'
              }}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => handleNavigation('/')}
              className={`transition-colors font-medium ${
                isActive('/') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => handleNavigation('/destinations')}
              className={`transition-colors font-medium ${
                isActive('/destinations') || location.pathname.includes('/destination/') 
                  ? 'text-amber-500' 
                  : 'text-white hover:text-amber-500'
              }`}
            >
              Destinations
            </button>
            <button
              onClick={() => handleNavigation('/visa')}
              className={`transition-colors font-medium ${
                isActive('/visa') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              Visa
            </button>
            <button
              onClick={() => handleNavigation('/instant-itinerary')}
              className={`transition-colors font-semibold inline-flex items-center gap-1 ${
                isActive('/instant-itinerary') ? 'text-amber-400' : 'text-amber-400 hover:text-amber-300'
              }`}
            >
              <Sparkles className="w-4 h-4" /> Instant Itinerary
            </button>
            <button
              onClick={() => handleNavigation('/services')}
              className={`transition-colors font-medium ${
                isActive('/services') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => handleNavigation('/blog')}
              className={`transition-colors font-medium ${
                isActive('/blog') || location.pathname.includes('/blog/')
                  ? 'text-amber-500'
                  : 'text-white hover:text-amber-500'
              }`}
            >
              Travel Blog
            </button>
            <button
              onClick={() => handleNavigation('/about')}
              className={`transition-colors font-medium ${
                isActive('/about') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              About Us
            </button>
            <Button
              onClick={() => handleNavigation('/contact')}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Contact Us
            </Button>
            <button
              onClick={() => setShowSearch(true)}
              data-testid="header-search-btn"
              aria-label="Search"
              title="Search"
              className="text-white hover:text-amber-500 transition-colors p-1"
            >
              <Search className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleNavigation(customer ? '/my-account' : '/login')}
              data-testid="header-account-link"
              aria-label={customer ? 'My Account' : 'Login'}
              title={customer ? 'My Account' : 'Login'}
              className={`relative transition-colors p-1 ${
                isActive('/my-account') || isActive('/login') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              <User className="w-5 h-5" />
              {customer && unreadCount > 0 && (
                <span data-testid="header-unread-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-sm py-6 space-y-4">
            <button
              onClick={() => handleNavigation('/')}
              className={`block w-full text-left font-medium px-4 py-2 ${
                isActive('/') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => handleNavigation('/destinations')}
              className={`block w-full text-left font-medium px-4 py-2 ${
                isActive('/destinations') || location.pathname.includes('/destination/')
                  ? 'text-amber-500' 
                  : 'text-white hover:text-amber-500'
              }`}
            >
              Destinations
            </button>
            <button
              onClick={() => handleNavigation('/visa')}
              className={`block w-full text-left font-medium px-4 py-2 ${
                isActive('/visa') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              Visa
            </button>
            <button
              onClick={() => handleNavigation('/instant-itinerary')}
              className="flex items-center gap-2 w-full text-left font-semibold px-4 py-2 text-amber-400 hover:text-amber-300"
            >
              <Sparkles className="w-4 h-4" /> Instant Itinerary
            </button>
            <button
              onClick={() => handleNavigation('/services')}
              className={`block w-full text-left font-medium px-4 py-2 ${
                isActive('/services') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => handleNavigation('/blog')}
              className={`block w-full text-left font-medium px-4 py-2 ${
                isActive('/blog') || location.pathname.includes('/blog/')
                  ? 'text-amber-500'
                  : 'text-white hover:text-amber-500'
              }`}
            >
              Travel Blog
            </button>
            <button
              onClick={() => handleNavigation('/about')}
              className={`block w-full text-left font-medium px-4 py-2 ${
                isActive('/about') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              About Us
            </button>
            <button
              onClick={() => { setIsMobileMenuOpen(false); setShowSearch(true); }}
              data-testid="header-search-btn-mobile"
              className="flex items-center gap-2 w-full text-left font-medium px-4 py-2 text-white hover:text-amber-500"
            >
              <Search className="w-4 h-4" /> Search
            </button>
            <div className="px-4">
              <Button
                onClick={() => handleNavigation('/contact')}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                Contact Us
              </Button>
            </div>
            <button
              onClick={() => handleNavigation(customer ? '/my-account' : '/login')}
              data-testid="header-account-link-mobile"
              className={`flex items-center gap-2 w-full text-left font-medium px-4 py-2 ${
                isActive('/my-account') || isActive('/login') ? 'text-amber-500' : 'text-white hover:text-amber-500'
              }`}
            >
              <User className="w-4 h-4" />
              {customer ? 'My Account' : 'Login'}
            </button>
          </div>
        )}
      </div>
      {showSearch && <SearchOverlay onClose={() => setShowSearch(false)} />}
    </header>
  );
};

export default Header;
